import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

// react router
import { useNavigate } from 'react-router-dom'

// styles imports
import Styles from './markets.module.css'

import { ReactComponent as BuyArrowIcon } from '../../../assets/icons/imported/buy-arrow.svg'
import { ReactComponent as SellArrowIcon } from '../../../assets/icons/imported/sell-arrow.svg'
import { formatNumber } from '../../helpers/formatNumber'

// APIs imports
import { _getCoinKline, _getMarkets24h } from '../redux/actions'

// redux
import { useSelector, useDispatch } from 'react-redux'
import { setSelectedCoin } from '../redux/slices/selectedCoin'
import { setSnackbarOpen, setSnackbarData } from '../../snackbar/redux/snackbarSlice'
import {
  setSelectCurrencyModalOpen,
  setSelectCurrencyModalData
} from '../../modals/select-currency/redux/selectCurrencyModalSlice'

// component imports
import Slider from 'react-slick'
import Convert from '../components/Convert'
import QuickBuySell from '../components/QuickBuySell'
import Search from '../../../components/Search/Search'
import CoinTag from '../../../components/CoinTag/CoinTag'
import { FormattedMessage, injectIntl } from 'react-intl'
import TableButton from '../../../components/TableButton/TableButton'
import MarketGraphCard from '../../../components/MarketGraphCard/MarketGraphCard'

// utilities
import { debounce } from '../../../utility/debounce'

// MUI
import MarketFooter from '../components/MarketFooter'
import WenbitModal from '../../../components/Modal/Modal'
import MUIDatagrid from '../../../components/MUIDatagrid/MUIDatagrid'

const convertCurrency = (toCurrency, fromCurrency, rate, amount) => {
  const formattedToCurrency = toCurrency?.toLowerCase()
  const formattedFromCurrency = fromCurrency?.toLowerCase()

  let convertedAmount
  if (formattedToCurrency === formattedFromCurrency) {
    convertedAmount = amount
  } else {
    convertedAmount = amount / rate
  }

  return convertedAmount
}

const createDefaultTickerData = () => {
  return {
    last: '0',
    price_change_percent: '+0.00%',
    price_change_percent_num: 0,
    vol: '0'
  }
}

const markets = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  // ----- RESPONSIVENESS STATE ---------
  const [modalStates, setModalStates] = useState({
    quickBuySellOpen: false,
    convertOpen: false
  })

  const [isTableCollapsed, setIsTableCollapsed] = useState(window.innerWidth < 940)
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 700)

  const handleResize = () => {
    setIsTableCollapsed(window.innerWidth < 900)
    setIsMobileView(window.innerWidth < 700)
  }

  const redirectToConvertHistory = id => {
    navigate(`/convert/${id.replace('/', '_')}`)
  }
  const redirectToTradeHistory = id => {
    navigate(`/trade/${id}`)
  }

  const openQuickTradeWithMarket = (market) => {
    // Trigger the select currency modal with the selected market as default
    dispatch(
      setSelectCurrencyModalData({
        title: 'Select Pair For Quick Trade',
        showTabs: false,
        type: 'all',
        showMarketPairs: true,
        searchPlaceholder: 'Search pair',
        currenciesToHide: [],
        disableBackdropClick: false,
        defaultSelectedMarket: market, // Pass the selected market as default
        callback: selectedMarket => {
          // Update the modal data with the newly selected market
          dispatch(
            setSelectCurrencyModalData({
              title: 'Select Pair For Quick Trade',
              showTabs: false,
              type: 'all',
              showMarketPairs: true,
              searchPlaceholder: 'Search pair',
              currenciesToHide: [],
              disableBackdropClick: false,
              defaultSelectedMarket: market,
              selectedMarket, // Store the newly selected market
              callback: () => { } // Prevent infinite callback loop
            })
          )
        }
      })
    )
    dispatch(setSelectCurrencyModalOpen(true))
  }
  const exchangeName = useSelector(state => state.exchangeCustomization.value.name)

  document.title = `${exchangeName}: Markets`

  const resetTable = () => {
    searchInputRef.current.value = ''
    fetchMarkets()
  }

  const handleRowClick = row => {
    dispatch(
      setSelectedCoin({
        id: row.row.id,
        name: row.row.name
      })
    )
    if (
      searchInputRef.current.value === '' ||
      searchInputRef.current.value === null ||
      searchInputRef.current.value === undefined
    )
      return
    resetTable()
  }

  const CarouselSettings = {
    infinite: true,
    speed: 900,
    autoplay: true,
    autoplaySpeed: 3000,
    slidesToShow: 3,
    slidesToScroll: 1,
    responsive: [
      {
        breakpoint: 1400,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1
        }
      },
      {
        breakpoint: 991,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1
        }
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1
        }
      }
    ]
  }

  useEffect(() => {
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const theme = useSelector(state => state.theme.value)
  const marketList = useSelector(state => state.marketList.value)
  const userData = useSelector(state => state.userData.value)
  const tickers = useSelector(state => state.marketTickers.value)
  const globalConversions = useSelector(state => state.globalConversions.value)
  const preferredCurrency = useSelector(state => state.preferredCurrency.value)

  const selectedCurrency = preferredCurrency?.id

  // pagination
  const [page, setPage] = useState(1)
  const pageLimit = 15
  const [marketRows, setMarketRows] = useState([])
  const [totalRows, setTotalRows] = useState(0)
  const [orderBy, setOrderBy] = useState(null)
  const [ordering, setOrdering] = useState(null)
  const [tableLoading, setTableLoading] = useState(false)
  const searchInputRef = useRef(null)
  const [klineArray, setKlineArray] = useState([])
  const [filteredMarketList, setFilteredMarketList] = useState(marketList)
  const [conversionsLoaded, setConversionsLoaded] = useState(false)
  const tickerDataRefs = useRef(new Map()).current

  const defaultTicker = {
    last: 0,
    vol: 0,
    price_change_percent: '+0.00%'
  }

  const fetchMarkets = useCallback(() => {
    const mounted = { current: true }
    setTableLoading(true)

    _getMarkets24h(
      {
        quote_unit: 'usd',
        order_by: orderBy,
        ordering,
        limit: pageLimit,
        page
      },
      ({ data, page, total }) => {
        if (mounted.current) {
          setFilteredMarketList(data)
          setTotalRows(total)
          setTableLoading(false)
        }
      },
      error => {
        if (mounted.current) {
          dispatch(setSnackbarOpen(true))
          dispatch(
            setSnackbarData({
              alertMessage: error.errors[0].length ? <FormattedMessage id={error.errors[0]} /> : 'Unknown Error',
              severity: 'error',
              callback: () => { },
              position: 'topRight'
            })
          )
        }
      }
    )

    return () => {
      mounted.current = false
    }
  }, [orderBy, ordering, page, pageLimit])

  const handlePageChange = (event, value) => {
    setPage(value)
  }

  useEffect(() => {
    const searchValue = searchInputRef.current.value
    if (searchValue === '' || searchValue === null || searchValue === undefined) {
      fetchMarkets()
    } else {
      setTableLoading(true)
      _getMarkets24h(
        {
          order_by: orderBy,
          ordering,
          page,
          limit: pageLimit,
          search: {
            quote_code: searchValue,
            base_name: searchValue
          }
        },
        ({ data, page, total }) => {
          setFilteredMarketList(data)
          setTotalRows(total)
          setTableLoading(false)
        },
        error => {
          setTableLoading(false)
          dispatch(setSnackbarOpen(true))
          dispatch(
            setSnackbarData({
              alertMessage: error.errors[0].length ? <FormattedMessage id={error.errors[0]} /> : 'Unknown Error',
              severity: 'error',
              callback: () => { },
              position: 'topRight'
            })
          )
        }
      )
    }
  }, [page, orderBy, ordering])

  const handleMarketSearch = e => {
    setTableLoading(true)
    const searchValue = e.target.value
    if (searchValue === '' || searchValue === null || searchValue === undefined) {
      setPage(1)
      fetchMarkets()
      return
    }
    _getMarkets24h(
      {
        quote_unit: 'usd',
        order_by: orderBy,
        ordering,
        page,
        limit: pageLimit,
        search: {
          base_code: searchValue,
          base_name: searchValue
        }
      },
      ({ data, page, total }) => {
        setFilteredMarketList(data)
        setTotalRows(total)
        setTableLoading(false)
      },
      error => {
        dispatch(setSnackbarOpen(true))
        dispatch(
          setSnackbarData({
            alertMessage: error.errors[0].length ? <FormattedMessage id={error.errors[0]} /> : 'Unknown Error',
            severity: 'error',
            callback: () => { },
            position: 'topRight'
          })
        )
      }
    )
  }

  const handleSortModelChange = sortData => {
    setOrdering(sortData[0].sort ? sortData[0].sort : null)
    setOrderBy(sortData[0].field ? sortData[0].field : null)
  }

  useEffect(() => {
    const mounted = { current: true }

    setPage(1)
    fetchMarkets()

    const marketIds = marketList
      .filter(market => market.quote_unit === 'usd' && market.last !== '0.0')
      .map(market => market.id)

    _getCoinKline(
      {
        pairs: marketIds,
        limit: 15
      },
      data => {
        if (mounted.current) {
          setKlineArray(data)
        }
      },
      err => console.warn('KLINE ERROR')
    )

    return () => {
      mounted.current = false
    }
  }, [])

  useEffect(() => {
    if (globalConversions?.length) {
      setConversionsLoaded(true)
    }
  }, [globalConversions])

  const transformedMarketData = useMemo(() => {
    if (!filteredMarketList) return []

    return filteredMarketList.map(market => {
      // Create ref if it doesn't exist
      if (!tickerDataRefs.has(market.id)) {
        tickerDataRefs.set(market.id, createDefaultTickerData())
      }

      return {
        ...market,
        id: market.id,
        tickerData: tickerDataRefs.get(market.id)
      }
    })
  }, [filteredMarketList])

  // Update ticker data without causing re-renders
  useEffect(() => {
    if (!tickers) return

    const mounted = { current: true }

    transformedMarketData?.forEach(market => {
      if (!mounted.current) return

      const ticker = tickers[market.id] || defaultTicker
      const existingTickerData = tickerDataRefs.get(market.id)
      if (existingTickerData) {
        // Create a new ticker data object instead of mutating the existing one
        // Use JSON parse/stringify to ensure we get a completely fresh object
        const newTickerData = {
          last: ticker.last || '0',
          price_change_percent: ticker.price_change_percent || '+0.00%',
          price_change_percent_num: Number.parseFloat(ticker.price_change_percent || '0'),
          vol: ticker.volume || ticker.vol || '0'
        }
        tickerDataRefs.set(market.id, newTickerData)
      }
    })

    return () => {
      mounted.current = false
    }
  }, [tickers, transformedMarketData])

  // Memoize expensive conversion calculation
  const getConvertedPrice = useCallback(
    price => {
      // if (!conversionsLoaded) return "Loading..."

      // const conversion = globalConversions.find(rate =>
      //   rate.currency_from === "USD" && rate.currency_to === preferredCurrency?.id
      // )

      // if (!conversion) return formatNumber(price, 3)

      // const convertedPrice = convertCurrency(
      //   preferredCurrency?.id,
      //   "USD",
      //   conversion.rate,
      //   price
      // )
      return formatNumber(price, 3)
    },
    [tickers]
  )

  const marketsTableColumns = isTableCollapsed
    ? [
      {
        field: 'base_unit',
        headerName: 'Coin Name',
        minWidth: 200,
        renderCell: data => (
          <span className={Styles.coinNameColumn}>
            <img src={data.row.icon_url} />
            {data.row.coin_name}
            <CoinTag tag={data.row.name.split('/')[0].toUpperCase()} size={11} />
          </span>
        ),
        flex: 1
      },
      {
        field: 'last',
        headerName: `Coin Price (${preferredCurrency?.id})`,
        editable: false,
        renderCell: data => (
          <span
            style={{
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <span>{getConvertedPrice(data.row.last)}</span>
            <span
              style={{
                color: data.row.price_change_percent[0] === '+' ? 'var(--positive-color)' : 'var(--negative-color)'
              }}
            >
              {data.row.price_change_percent}
              {data.row.price_change_percent[0] === '+' ? <BuyArrowIcon width={13} /> : <SellArrowIcon width={13} />}
            </span>
          </span>
        )
      }
    ]
    : [
      {
        field: 'base_unit',
        headerName: 'Coin Name',
        minWidth: 145,
        renderCell: data => (
          <span className={Styles.coinNameColumn}>
            <img src={data.row.icon_url} />
            {data.row.coin_name}
            <CoinTag tag={data.row.name.split('/')[0].toUpperCase()} size={11} />
          </span>
        ),
        flex: 1
      },
      {
        field: 'last',
        headerName: `Coin Price (${preferredCurrency?.id})`,
        minWidth: 125,
        editable: false,
        renderCell: data => <span>{getConvertedPrice(data.row.last)}</span>
      },
      {
        field: 'price_change_percent',
        headerName: '24%',
        editable: false,
        renderCell: data => (
          <span
            style={{
              color: data.row.price_change_percent[0] === '+' ? 'var(--positive-color)' : 'var(--negative-color)'
            }}
          >
            {data.row.price_change_percent}
            {data.row.price_change_percent[0] === '+' ? <BuyArrowIcon width={13} /> : <SellArrowIcon width={13} />}
          </span>
        ),
        headerClassName: Styles.columnToHide,
        cellClassName: Styles.columnToHide
      },
      {
        field: 'high',
        headerName: '24h High',
        editable: false,
        renderCell: data => <>{`${getConvertedPrice(data.row.high)}`}</>,
        headerClassName: Styles.columnToHide,
        cellClassName: Styles.columnToHide
      },
      {
        field: 'low',
        headerName: '24h Low',
        editable: false,
        renderCell: data => <>{`${getConvertedPrice(data.row.low)}`}</>,
        headerClassName: Styles.columnToHide,
        cellClassName: Styles.columnToHide
      },
      {
        field: 'action',
        headerName: 'Action',
        sortable: false,
        width: 180,
        editable: false,
        renderCell: data => (
          <span className={Styles.actionColumn}>
            <TableButton
              buttonText='Trade'
              // buttonIcon={<LinkIcon width={11} />}
              onClick={() => openQuickTradeWithMarket(data.row)}
            />
            <TableButton
              buttonText='Convert'
              // buttonIcon={<LinkIcon width={11} />}
              onClick={() => redirectToConvertHistory(data.row.name)}
            />
          </span>
        ),
        headerClassName: Styles.columnToHide,
        cellClassName: Styles.columnToHide
      }
    ]

  const MemoizedMarketGraphCard = React.memo(({ market, klineData }) => (
    <MarketGraphCard
      key={market.coin_name}
      icon={market.icon_url}
      pairId={market.id}
      coinName={market.coin_name.toUpperCase()}
      price={market.last}
      priceChangePercentage={market.price_change_percent}
      marketKLine={klineData}
      containerStyle={{ margin: '0 6px' }}
    />
  ))

  // Set the displayName for debugging purposes
  MemoizedMarketGraphCard.displayName = 'MemoizedMarketGraphCard'

  return (
    <div className={Styles.pageContainer}>
      {/* left section container */}
      <div className={Styles.leftSectionContainer}>
        {/* {isMobileView && <Convert />} */}

        {/* market coins card */}
        <div
          className={Styles.card}
          style={{
            display: isMobileView && 'none'
          }}
        >
          {/* card header */}
          <div className={Styles.cardHeader}>
            {/* left section */}
            <div className={Styles.cardHeaderLeft}>
              <div className={Styles.cardTitle}>
                <FormattedMessage id='page.markets.trade.marketCoins' />
              </div>
              <div className={Styles.cardSubTitle}>Glorious, not only for your eyes.</div>
            </div>

            {/* right section */}
            <div className={Styles.cardHeaderRight}>
              {!isMobileView && (
                <Search
                  placeholder='Search Coin Name'
                  onChange={debounce(handleMarketSearch, 500)}
                  ref={searchInputRef}
                />
              )}
            </div>
          </div>

          {/* card graph marquees container */}
          {/* {!isTableCollapsed && ( */}
          <div className={Styles.graphMarqueesContainer}>
            <Slider {...CarouselSettings} arrows={false}>
              {marketList
                .filter(market => market.quote_unit === selectedCurrency?.toLowerCase() && market.last !== '0.0')
                .map(market => {
                  const klineData = klineArray?.find(el => Object.keys(el).includes(market.id))
                  return <MemoizedMarketGraphCard key={market.id} market={market} klineData={klineData} />
                })}
            </Slider>
          </div>
          {/* )} */}
        </div>

        {/* markets list card */}
        <div className={Styles.card}>
          {isMobileView && (
            <Search
              placeholder='Search Coin Name'
              onChange={debounce(handleMarketSearch, 500)}
              ref={searchInputRef}
              containerStyle={{
                width: '100%',
                marginBottom: 25
              }}
              style={{ fontSize: 15 }}
            />
          )}

          {/* Datagrid */}
          <MUIDatagrid
            disableSorting
            page={page}
            pageLimit={pageLimit}
            rows={transformedMarketData.filter(market => market.tickerData.last !== '0.0')}
            totalRowsCount={totalRows}
            loading={tableLoading}
            onRowClick={handleRowClick}
            onPageChange={handlePageChange}
            onSortModelChange={handleSortModelChange}
            getRowId={row => row.id}
            columns={marketsTableColumns}
            tableHeight={isTableCollapsed ? (isMobileView ? 930 : 997) : 930}
          />
        </div>
      </div>

      {/* right section container */}
      {!isMobileView && (
        <div className={Styles.rightSectionContainer}>
          {/* <BuyCrypto /> */}
          {/* <Convert /> */}
          <QuickBuySell selectedCurrency={selectedCurrency} />
        </div>
      )}

      {isMobileView && userData.email && <MarketFooter setModalStates={setModalStates} />}

      <WenbitModal
        open={modalStates.quickBuySellOpen}
        modalWidth='90%'
        render={() => (
          <QuickBuySell
            containerStyle={{
              backgroundColor: 'transparent'
            }}
            hideTitle
          />
        )}
        onClose={() =>
          setModalStates({
            quickBuySellOpen: false,
            convertOpen: false
          })
        }
        title='Quick Order'
      />
      <WenbitModal
        open={modalStates.convertOpen}
        modalWidth='90%'
        render={() => (
          <Convert
            hideTitle
            containerStyle={{
              backgroundColor: 'transparent'
            }}
          />
        )}
        onClose={() =>
          setModalStates({
            quickBuySellOpen: false,
            convertOpen: false
          })
        }
        title='Convert'
      />
    </div>
  )
}

export default injectIntl(markets)
